import { useMemo } from "react";
import { toast } from "sonner";
import { Download, Copy, Rocket, Megaphone, Linkedin, Share2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SECTION_DEFS = [
  { key: "linkedin", label: "LINKEDIN POST" },
  { key: "google", label: "GOOGLE ADS" },
  { key: "email", label: "EMAIL OUTREACH" },
  { key: "social", label: "SOCIAL POST" },
  { key: "flyer", label: "FLYER COPY" },
];

const isHeaderLine = (line) => {
  let t = line.trim().replace(/[*#_]/g, "").trim();
  t = t.replace(/^\d+\s*[).:\-]\s*/, "");
  if (!t || /^[a-z]/.test(t)) return null;
  const head = t.toUpperCase();
  const hit = SECTION_DEFS.find((s) => {
    if (!head.startsWith(s.label)) return false;
    const rest = t.slice(s.label.length).trimStart();
    return rest === "" || /^[—–:•\-–—]/.test(rest);
  });
  return hit ? hit.key : null;
};

export const parseSections = (text) => {
  const lines = text.split("\n");
  const sections = {};
  let current = null;
  for (const line of lines) {
    const header = isHeaderLine(line);
    if (header) {
      current = header;
      if (!sections[current]) sections[current] = [];
      continue;
    }
    if (current) sections[current].push(line);
  }
  const out = {};
  for (const [k, v] of Object.entries(sections)) {
    out[k] = v.join("\n").trim();
  }
  return out;
};

const extractList = (sectionText, kind) => {
  const lines = sectionText.split("\n").map((l) => l.trim()).filter(Boolean);
  const items = [];
  let capturing = false;
  for (const line of lines) {
    const clean = line.replace(/[*#]/g, "").trim();
    if (new RegExp(kind, "i").test(clean) && clean.length < 40) {
      capturing = true;
      continue;
    }
    if (capturing) {
      const m = clean.match(/^(?:\d+[.)]|[-•])\s*[""]?(.+?)[""]?$/);
      if (m) {
        items.push(m[1].trim());
        continue;
      }
      if (items.length > 0) break;
    }
  }
  return items;
};

const download = (filename, content, type = "text/plain") => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const GUIDES = {
  google: [
    "Go to ads.google.com and create a free Google Ads account (skip the guided 'Smart' campaign; choose Expert Mode).",
    "Create a new Search campaign — objective: Leads.",
    "In Location targeting, enter United States and REMOVE every other location so ads show in the USA only.",
    "Set a starting daily budget ($10–20/day works for local clinic searches).",
    "Open the downloaded CSV and paste the headlines and descriptions into your Responsive Search Ad.",
    "Add keywords like: virtual medical assistant, medical office assistant services, HIPAA virtual assistant.",
    "Review and Publish — ads usually start showing within a few hours of approval.",
  ],
  linkedin: [
    "Go to linkedin.com/campaignmanager and create a free ad account linked to your company page.",
    "Create a new campaign — objective: Website Visits or Lead Generation.",
    "Audience → Location: United States only.",
    "Add job titles: Practice Manager, Medical Director, Clinic Owner, Physician; industry: Hospitals & Health Care.",
    "Create a Single Image Ad and paste the intro text and headline from the downloaded sheet.",
    "Set a daily budget ($15+/day recommended on LinkedIn) and launch.",
  ],
  social: [
    "Go to facebook.com/adsmanager and create a free ad account (needs a Facebook Page for Verma Med Associates).",
    "Create a campaign — objective: Leads or Traffic.",
    "In the ad set, set Location: United States only.",
    "Detailed targeting: interests like Medical practice, Health care administration, Private practice.",
    "Create the ad, paste the post text and hashtags from the downloaded pack, and add your site link.",
    "Set $5–10/day, then Publish — the same ad runs on Facebook and Instagram.",
  ],
};

const PlatformCard = ({ icon: Icon, title, content, onDownload, downloadLabel, guide, testid }) => (
  <div className="rounded-3xl border border-white/10 bg-[#0A261E] p-6" data-testid={`launch-card-${testid}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAB308]/15">
          <Icon className="h-4 w-4 text-[#EAB308]" />
        </div>
        <p className="font-display text-base font-bold text-[#F5F7F6]">{title}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => {
            navigator.clipboard.writeText(content);
            toast.success(`${title} content copied`);
          }}
          data-testid={`launch-copy-${testid}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#F5F7F6] hover:bg-white/10 transition-colors"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
        <button
          onClick={onDownload}
          data-testid={`launch-download-${testid}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#EAB308] px-3 py-1.5 text-xs font-bold text-[#0A261E] transition-all hover:brightness-110"
        >
          <Download className="h-3 w-3" /> {downloadLabel}
        </button>
      </div>
    </div>
    <pre className="mt-4 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-[#123328] p-4 font-sans text-xs leading-relaxed text-[#F5F7F6]/85" data-testid={`launch-content-${testid}`}>
      {content}
    </pre>
    <Accordion type="single" collapsible className="mt-4">
      <AccordionItem value="guide" className="border-white/10" data-testid={`launch-guide-${testid}`}>
        <AccordionTrigger className="py-3 text-xs font-semibold text-[#EAB308] hover:no-underline">
          Step-by-step launch guide (about 10 minutes)
        </AccordionTrigger>
        <AccordionContent>
          <ol className="space-y-2.5 pb-2">
            {guide.map((step, i) => (
              <li key={i} className="flex gap-3 text-xs leading-relaxed text-[#A3B8B0]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EAB308]/15 font-mono text-[10px] font-semibold text-[#EAB308]">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
);

export const LaunchKit = ({ output }) => {
  const sections = useMemo(() => parseSections(output), [output]);
  const siteUrl = window.location.origin;

  const googleCsv = useMemo(() => {
    const g = sections.google || output;
    const headlines = extractList(g, "headline").slice(0, 15);
    const descriptions = extractList(g, "description").slice(0, 4);
    const esc = (s) => `"${s.replace(/"/g, '""')}"`;
    const rows = [
      ["Campaign", "Ad Group", "Asset Type", "Text", "Final URL", "Geo Target"],
      ["Verma Med — USA", "Virtual Medical Assistants", "SETTING", "Location: United States only", siteUrl, "United States"],
      ...headlines.map((h) => ["Verma Med — USA", "Virtual Medical Assistants", "Headline", h, siteUrl, "United States"]),
      ...descriptions.map((d) => ["Verma Med — USA", "Virtual Medical Assistants", "Description", d, siteUrl, "United States"]),
    ];
    if (headlines.length === 0 && descriptions.length === 0) {
      rows.push(["Verma Med — USA", "Virtual Medical Assistants", "RAW AD COPY", g.slice(0, 2000), siteUrl, "United States"]);
    }
    return rows.map((r) => r.map(esc).join(",")).join("\n");
  }, [sections, output, siteUrl]);

  const linkedinSheet = useMemo(() => {
    const body = sections.linkedin || "(LinkedIn section not found — see full campaign above)";
    return [
      "VERMA MED ASSOCIATES — LINKEDIN CAMPAIGN SHEET (USA ONLY)",
      "=".repeat(56),
      "",
      "CAMPAIGN OBJECTIVE: Website Visits / Lead Generation",
      "LOCATION TARGETING: United States only",
      "AUDIENCE: Practice Manager, Medical Director, Clinic Owner, Physician",
      "INDUSTRY: Hospitals & Health Care",
      "",
      "AD INTRO TEXT + HEADLINE:",
      "-".repeat(56),
      body,
      "",
      `LANDING PAGE: ${siteUrl}`,
    ].join("\n");
  }, [sections, siteUrl]);

  const socialPack = useMemo(() => {
    const body = sections.social || "(Social section not found — see full campaign above)";
    return [
      "VERMA MED ASSOCIATES — FACEBOOK + INSTAGRAM POST PACK (USA ONLY)",
      "=".repeat(56),
      "",
      "CAMPAIGN OBJECTIVE: Leads / Traffic",
      "LOCATION TARGETING: United States only",
      "INTERESTS: Medical practice, Health care administration, Private practice",
      "",
      "POST TEXT + HASHTAGS:",
      "-".repeat(56),
      body,
      "",
      `LINK: ${siteUrl}`,
    ].join("\n");
  }, [sections, siteUrl]);

  return (
    <div className="rounded-3xl border-2 border-[#EAB308]/50 bg-[#123328] p-7 sm:p-8" data-testid="launch-kit">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EAB308]">
          <Rocket className="h-5 w-5 text-[#0A261E]" />
        </div>
        <div>
          <p className="font-display text-xl font-bold text-[#F5F7F6]">One-Click Launch Kit</p>
          <p className="text-xs text-[#A3B8B0]">
            Ready-to-paste exports for each platform — all targeting the USA only.
          </p>
        </div>
      </div>
      <div className="mt-6 space-y-5">
        <PlatformCard
          icon={Megaphone}
          title="Google Ads"
          content={sections.google || "See full campaign output above."}
          downloadLabel="CSV"
          testid="google"
          guide={GUIDES.google}
          onDownload={() => {
            download("verma-med-google-ads-usa.csv", googleCsv, "text/csv");
            toast.success("Google Ads CSV downloaded");
          }}
        />
        <PlatformCard
          icon={Linkedin}
          title="LinkedIn"
          content={sections.linkedin || "See full campaign output above."}
          downloadLabel="Sheet"
          testid="linkedin"
          guide={GUIDES.linkedin}
          onDownload={() => {
            download("verma-med-linkedin-campaign-usa.txt", linkedinSheet);
            toast.success("LinkedIn campaign sheet downloaded");
          }}
        />
        <PlatformCard
          icon={Share2}
          title="Facebook + Instagram"
          content={sections.social || "See full campaign output above."}
          downloadLabel="Pack"
          testid="social"
          guide={GUIDES.social}
          onDownload={() => {
            download("verma-med-social-pack-usa.txt", socialPack);
            toast.success("Social post pack downloaded");
          }}
        />
      </div>
    </div>
  );
};
